import { useEvent } from '@emerald-works/react-event-bus-client'
import { ChevronLeft, ChevronRight } from '@mui/icons-material'
import { Box, Card, CardContent, IconButton, Tab, Tabs, Typography } from '@mui/material'
import React, { useCallback, useState } from 'react'
import { useDispatch } from 'react-redux'
import { Link } from 'react-router-dom'
import { useFormFactor } from '../../../../hooks'
import { homeEnterpriseSlice } from '../../../../reducers'
import { routes } from '../../../../router/routes'
import SkillBuilderCard from '../../../msb/components/cards/skill-builder-card'
import { useScrollableRails } from '../../hooks/useScrollableRails'
import { useTrackHomeEnterpriseEvent } from '../../hooks/useTrackHomeEnterpriseEvent'
import { HomeRailsScrollableContainer } from '../home-rails-scrollable-container'

function TabPanel (props) {
  const { children, selected, value, ...other } = props

  return (
    <Box
      role='tabpanel'
      hidden={selected !== value}
      id={`home-msb-tabpanel-${value}`}
      aria-labelledby={`home-msb-tab-${value}`}
      {...other}
    >
      {selected === value && <Box sx={{ pt: 2 }}>{children}</Box>}
    </Box>
  )
}

function a11yProps (index) {
  return {
    id: `home-msb-tab-${index}`,
    'aria-controls': `home-msb-tabpanel-${index}`
  }
}

export const MSBRails = ({ msbSection }) => {
  const { skills, id, name } = msbSection
  const [selectedTab, setSelectedTab] = useState(skills[0]?.id)
  const { isMobile } = useFormFactor()
  const dispatch = useDispatch()
  const { trackSkillBuilderClickEvent } = useTrackHomeEnterpriseEvent()
  const [courseProgressLoading, setCourseProgressLoading] = useState({})

  const selectedSkill = skills.find(child => child.id === selectedTab)

  const dataTest = 'home-msb-rails'

  const {
    scrollContainerRef,
    canScrollLeft,
    canScrollRight,
    hasScrollableContent,
    scrollLeft,
    scrollRight,
    updateScrollState
  } = useScrollableRails(selectedSkill?.items || [])

  const handleChange = (event, newValue) => {
    setSelectedTab(newValue)
  }

  const [getSkillBuilderUserProgress] = useEvent([{
    eventName: 'getSkillBuilderUserProgress',
    onSuccess: (payload) => {
      dispatch(homeEnterpriseSlice.actions.setMsbCourseProgress(payload))
      setCourseProgressLoading(prev => ({
        ...prev,
        [payload.courseId]: false
      }))
    }
  }])

  const onRefetchMSBCourseProgress = useCallback((courseId) => {
    setCourseProgressLoading(prev => ({
      ...prev,
      [courseId]: true
    }))
    setTimeout(() => {
      getSkillBuilderUserProgress.trigger({ courseId })
    }, 2000) // Leave space for the progress to be updated
  }, [getSkillBuilderUserProgress])

  const exploreMoreCta = selectedSkill && (
    <Typography variant={!isMobile ? 'caption' : undefined}>
      Explore <Link to={routes.msb.path}>All Manager Skill Builder Items</Link>
    </Typography>
  )

  return (
    <Card data-test={dataTest} sx={{ backgroundColor: (theme) => theme.palette.grey.shade[3] }}>
      <CardContent sx={{ m: 0, p: 0, '&:last-child': { pb: 0 } }}>
        <Box sx={{ display: 'flex', flexDirection: 'row', p: 2, backgroundColor: (theme) => theme.palette.grey.shade[7], alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid #E0E0E0' }}>
          <Typography sx={{ fontWeight: 700 }}>
            Improve your managerial skills in just 45 minutes
          </Typography>
          {!isMobile && exploreMoreCta}
        </Box>
        <Box sx={{ display: 'flex', alignContent: 'center', justifyContent: 'space-between' }}>
          <Tabs
            value={selectedTab}
            onChange={handleChange}
            aria-label='home page msb tabs'
            sx={{
              borderBottom: 'none',
              '.MuiTabs-scrollButtons.Mui-disabled': {
                opacity: 0.3
              }
            }}
            variant='scrollable'
            scrollButtons='auto'
            allowScrollButtonsMobile
          >
            {skills.map((child, index) => (
              <Tab
                key={child.id}
                label={child.name}
                {...a11yProps(index)}
                value={child.id}
              />
            ))}
          </Tabs>
          {!isMobile && hasScrollableContent && (
            <Box display='flex' alignItems='center' data-test={`${dataTest}-scroll-buttons-container`} justifyContent='end'>
              {/* Left scroll arrow */}
              <IconButton
                onClick={scrollLeft}
                size='small'
                disabled={!canScrollLeft}
                data-test={`${dataTest}-scroll-left-button`}
              >
                <ChevronLeft />
              </IconButton>

              {/* Right scroll arrow */}
              <IconButton
                onClick={scrollRight}
                size='small'
                data-test={`${dataTest}-scroll-right-button`}
                disabled={!canScrollRight}
              >
                <ChevronRight />
              </IconButton>
            </Box>
          )}
        </Box>
        {skills.map((skill, index) => (
          <TabPanel key={skill.id} selected={selectedTab} value={skill.id}>

            <Box sx={{ position: 'relative' }}>

              <HomeRailsScrollableContainer
                data-test={`${dataTest}-scrollable-container`}
                scrollContainerRef={scrollContainerRef}
                onScroll={updateScrollState}
              >
                {skill.items.map((item, itemIndex) => (
                  <Box
                    key={item.sk}
                    sx={{
                      minWidth: 300,
                      maxWidth: 300,
                      flexShrink: 0
                    }}
                  >
                    <SkillBuilderCard
                      skillBuilderLearningItem={item}
                      isLoading={false}
                      isLoadingProgress={courseProgressLoading[item.id]}
                      onRefetchMSBCourseProgress={onRefetchMSBCourseProgress}
                      onLaunchCourse={(item) => {
                        trackSkillBuilderClickEvent(item.id, {
                          skillBuilderItemId: item.id,
                          skillBuilderItemTitle: item.title,
                          skillBuilderMetaSkillId: id,
                          skillBuilderMetaSkillName: name,
                          skillBuilderIndex: index,
                          skillBuilderSkillId: selectedSkill.id,
                          skillBuilderSkillName: selectedSkill.name,
                          skillBuilderItemIndex: itemIndex
                        })
                      }}
                    />
                  </Box>
                ))}
              </HomeRailsScrollableContainer>
              {isMobile && (
                <Box sx={{ mt: 2, px: 2, pb: 2 }}>
                  {exploreMoreCta}
                </Box>
              )}
            </Box>
          </TabPanel>
        ))}
      </CardContent>
    </Card>
  )
}
